import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listHighlights, deleteHighlights } from "@/lib/api";
import { Highlighter, ArrowLeft, Trash2 } from "lucide-react";

const PALETTE = [
    { name: "sage", value: "#B7CDB1" },
    { name: "amber", value: "#E7C36F" },
    { name: "plum", value: "#C7A0B0" },
    { name: "slate", value: "#9CB0C0" },
    { name: "clay", value: "#D8B49E" },
];

export default function HighlightsPage() {
    const [color, setColor] = useState("all");
    const [items, setItems] = useState([]);
    const nav = useNavigate();

    const load = () => {
        const params = color !== "all" ? { color } : {};
        listHighlights(params).then(d => setItems(d.highlights || []));
    };
    useEffect(() => { load(); }, [color]); // eslint-disable-line

    return (
        <div className="px-5 md:px-8 py-6 md:py-8 pb-32 mx-auto w-full" style={{ maxWidth: "72ch" }}>
            <button onClick={() => nav("/read")} className="inline-flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink mb-3">
                <ArrowLeft size={14} /> Back to reader
            </button>
            <div className="flex items-center gap-3 mb-4">
                <Highlighter size={22} className="text-moss-deep" />
                <h1 className="scripture text-3xl md:text-4xl text-ink tracking-tight">Highlights</h1>
            </div>

            <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar py-1">
                <ColorChip active={color === "all"} onClick={() => setColor("all")}>All</ColorChip>
                {PALETTE.map(c => (
                    <ColorChip
                        key={c.name}
                        testid={`filter-color-${c.name}`}
                        active={color === c.name}
                        onClick={() => setColor(c.name)}
                        swatch={c.value}
                    >
                        {c.name.charAt(0).toUpperCase() + c.name.slice(1)}
                    </ColorChip>
                ))}
            </div>

            {items.length === 0 ? (
                <div className="mt-10 text-center">
                    <p className="scripture text-[18px] text-ink-70">Nothing highlighted yet.</p>
                    <p className="mt-1 font-ui text-[13px] text-ink-40">Long-press a verse in the reader to begin.</p>
                </div>
            ) : (
                <ul className="space-y-2">
                    {items.map(h => (
                        <li key={h.id} className="p-4 rounded-2xl bg-white border border-ink-10 hover:border-moss/40 hover:shadow-md transition">
                            <div className="flex items-start justify-between gap-3">
                                <Link
                                    data-testid={`highlight-item-${h.id}`}
                                    to={`/?chapter=${h.chapter || 1}${h.verse ? `&verse=${h.verse}` : ""}`}
                                    className="flex-1 min-w-0"
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="inline-block w-3 h-3 rounded-full"
                                            style={{ background: swatchFor(h.color) }}
                                            aria-label={h.color}
                                        />
                                        <span className="font-ui text-[11px] uppercase tracking-[0.2em] text-moss-deep font-semibold">
                                            {h.label}
                                        </span>
                                    </div>
                                    {h.preview && (
                                        <p className="scripture text-[15px] text-ink mt-1 leading-snug line-clamp-3">
                                            {h.preview}
                                        </p>
                                    )}
                                </Link>
                                <button
                                    data-testid={`highlight-delete-${h.id}`}
                                    onClick={async () => { await deleteHighlights(h.refs); load(); }}
                                    className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-ink-40 hover:text-plum hover:bg-plum/10 transition"
                                    aria-label="Delete highlight"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ColorChip({ testid, active, onClick, swatch, children }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={
                "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-ui text-[12px] transition " +
                (active
                    ? "bg-ink text-white shadow-sm"
                    : "bg-white text-ink-70 border border-ink-10 hover:text-ink hover:border-moss/40")
            }
        >
            {swatch && <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: swatch }} />}
            {children}
        </button>
    );
}

function swatchFor(name) {
    return PALETTE.find(p => p.name === name)?.value || "#ccc";
}
